/**
 * Meu Perfil — ponto único de edição dos dados do paciente.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, User } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types/auth';
import { Sidebar } from '@/components/common/Sidebar';
import { getPatientByUserId, type PatientDTO } from '@/services/patients';
import { PatientProfileEditor } from '@/components/dashboard/PatientProfileEditor';
import { toast } from 'sonner';

export default function MeuPerfilPage() {
  const navigate = useNavigate();
  const { user, isLoading, logout, updateSessionUser } = useAuth();
  const [patient, setPatient] = useState<PatientDTO | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== UserRole.PATIENT) {
      navigate('/settings', { replace: true });
    }
  }, [user, isLoading, navigate]);

  useEffect(() => {
    if (!user || user.role !== UserRole.PATIENT) return;

    const load = async () => {
      setLoadingProfile(true);
      setError(null);
      try {
        const response = await getPatientByUserId(user.id);
        if (response.success && response.data) {
          setPatient(response.data);
        } else {
          setError(response.error || 'Não foi possível carregar seu perfil.');
        }
      } catch (err) {
        console.error(err);
        setError('Erro ao carregar perfil.');
      } finally {
        setLoadingProfile(false);
      }
    };

    load();
  }, [user]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch {
      toast.error('Erro ao sair');
    }
  };

  if (isLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-950">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex">
      <Sidebar userRole={user.role} userName={user.name} onLogout={handleLogout} />

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800 px-6 sm:px-8 py-6">
          <div className="flex items-center gap-3">
            <User className="w-8 h-8 text-teal-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Meu Perfil</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Atualize seus dados pessoais e clínicos. Eles refletem no prontuário.
              </p>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto px-6 sm:px-8 py-8">
          <div className="max-w-4xl mx-auto">
            {loadingProfile && (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-teal-600 animate-spin mb-4" />
                <p className="text-sm text-gray-500">Carregando perfil...</p>
              </div>
            )}

            {!loadingProfile && error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 dark:bg-rose-950/20 dark:border-rose-900 p-6 text-center">
                <p className="text-rose-700 dark:text-rose-300 font-semibold">{error}</p>
                <button
                  type="button"
                  className="mt-4 text-sm font-semibold text-teal-700"
                  onClick={() => window.location.reload()}
                >
                  Tentar novamente
                </button>
              </div>
            )}

            {!loadingProfile && patient && (
              <PatientProfileEditor
                patient={patient}
                accountName={user.name}
                accountEmail={user.email}
                onUpdate={setPatient}
                onAccountEmailUpdated={updateSessionUser}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
